import { useId } from 'react';
import { X } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { WarehouseLineControl } from '@/hooks/useWarehouseLine';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { WarehouseFieldStatus } from './WarehouseFieldStatus';
import { WAREHOUSE_LINES, isWarehouseValue, matchWarehouseLine } from '../../../../supabase/functions/_shared/warehouse-line-contract';

export function WarehouseLineSelect({ control, draft = false, compact = false, disabled = false }: {
  control: WarehouseLineControl; draft?: boolean; compact?: boolean; disabled?: boolean;
}) {
  const id = useId();
  const value = draft ? control.draftValue : control.value;
  const choose = (next: string) => {
    if (next === value || !isWarehouseValue(next)) {
      return;
    }
    if (draft) {
      control.setDraft(next);
    } else {
      void control.save(next);
    }
  };
  const selected = matchWarehouseLine(value) ?? '';
  const blocked = disabled || control.blocked;
  const message = [draft && control.isDirty ? 'Unsaved warehouse change' : '', control.status,
    control.readError, control.paused ? 'Automatic checks paused.' : ''].filter(Boolean).join('\n')
    || (value ? 'Warehouse recorded in Encompass' : 'Select a warehouse');
  return (
    <div className="min-w-0" data-warehouse-control data-row-interactive="true" onClick={event => event.stopPropagation()}>
      {!compact && <label htmlFor={id} className="mb-1 block text-xs text-muted-foreground uppercase tracking-wide">Warehouse</label>}
      <div className="flex h-8 min-w-0 items-center gap-1">
        <Select value={selected} onValueChange={choose} disabled={blocked}>
          <SelectTrigger id={id} aria-label="Warehouse" title={value || undefined}
            className={cn('h-8 min-w-0 flex-1 px-2', compact ? 'text-xs' : 'text-sm')}>
            <SelectValue placeholder={value || 'Select warehouse'} />
          </SelectTrigger>
          <SelectContent>
            {WAREHOUSE_LINES.map(line => <SelectItem key={line} value={line}>{line}</SelectItem>)}
          </SelectContent>
        </Select>
        {value ? <TooltipProvider><Tooltip><TooltipTrigger asChild>
          <Button type="button" variant="ghost" size="icon" disabled={blocked}
            className="h-6 w-6 shrink-0 p-0 text-muted-foreground" aria-label="Clear warehouse" onClick={() => choose('')}>
            <X aria-hidden="true" className="h-3.5 w-3.5" />
          </Button>
        </TooltipTrigger><TooltipContent>Clear warehouse</TooltipContent></Tooltip></TooltipProvider>
          : <span className="w-6 shrink-0" aria-hidden="true" />}
        <WarehouseFieldStatus label="Warehouse status" message={message} tone={draft && control.isDirty ? 'warning' : control.statusTone}>
          <div className="flex flex-wrap gap-2">
            {control.canRetry && <Button type="button" variant="outline" size="sm" className="h-8 px-2 text-xs"
              onClick={() => void control.retry()}>Retry</Button>}
            {control.needsCheck && <Button type="button" variant="ghost" size="sm" className="h-8 px-2 text-xs"
              disabled={control.checking} onClick={() => void control.check()}>Check again</Button>}
          </div>
        </WarehouseFieldStatus>
      </div>
    </div>
  );
}
